/**
 * Self-contained, dependency-free preview-safety helpers for support ticket
 * attachments. This module MUST NOT import the API/runtime layer so it can be
 * unit-tested in isolation and reused by both the API service and the preview
 * composable/component as the single source of truth for what may be rendered.
 */

/**
 * Inline-preview kinds we are willing to render inside the app.
 * Everything else is download-only.
 */
export type SupportAttachmentPreviewKind = 'image' | 'pdf' | 'text';

/**
 * Extension allow-list per preview kind. Extension is a cheap first gate; the
 * fetched blob's MIME type is cross-checked against the MIME allow-list below.
 * SVG is intentionally excluded from images (it can carry active script).
 */
export const SUPPORT_ATTACHMENT_PREVIEW_EXTENSIONS: Record<SupportAttachmentPreviewKind, string[]> = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif'],
  pdf: ['pdf'],
  // Inert, text-based formats that are safe to render inline. They are shown
  // through text interpolation (never parsed as markup) after control/bidi
  // sanitization, so none of them can execute or be interpreted. Python source
  // and the safe config/data formats are previewable as plain text; deliberately
  // NO markup/active types (html, xhtml, svg, js, css, vue, php, …).
  text: ['txt', 'csv', 'log', 'md', 'json', 'py', 'yaml', 'yml', 'toml', 'ini', 'xml']
};

/**
 * MIME allow-list per preview kind. The server-declared Content-Type (read from
 * the fetched Blob) MUST match one of these before we render inline, so a file
 * with a misleading extension cannot be rendered as a different type.
 *
 * Note: `image/svg+xml` is deliberately NOT an allowed image type. SVG can carry
 * inline script and, when rendered from a blob URL or opened in a new tab, would
 * execute in a browsing context. Only inert raster image types are permitted.
 */
export const SUPPORT_ATTACHMENT_PREVIEW_MIME: Record<SupportAttachmentPreviewKind, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/avif'],
  pdf: ['application/pdf'],
  // Concrete inert text MIME types a server may return for the previewable text
  // formats. Anything starting with `text/` is also accepted (see the gate
  // below); these cover the `application/*` text formats. Active/markup types
  // (application/xhtml+xml, image/svg+xml, JS/PHP/WASM) are refused via
  // SUPPORT_ATTACHMENT_ACTIVE_MIME_TYPES and are never listed here.
  text: [
    'text/plain',
    'text/csv',
    'text/markdown',
    'text/x-markdown',
    'text/x-log',
    'text/x-python',
    'text/x-python-script',
    'text/x-yaml',
    'text/yaml',
    'text/xml',
    'text/toml',
    'application/json',
    'application/x-python-code',
    'application/x-yaml',
    'application/yaml',
    'application/toml',
    'application/xml'
  ]
};

/**
 * Active/markup content types that must never be treated as a safe preview, even
 * though some nominally start with `text/`. Rendering (or opening) these as their
 * real type could execute script; the preview refuses them outright.
 */
export const SUPPORT_ATTACHMENT_ACTIVE_MIME_TYPES: ReadonlySet<string> = new Set([
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
  'text/javascript',
  'application/javascript',
  'application/ecmascript',
  'text/ecmascript',
  'application/x-httpd-php',
  'application/wasm'
]);

/**
 * Maximum bytes rendered inline for a text preview. A hard cap protects the
 * renderer from multi-megabyte files that would freeze the tab.
 */
export const MAX_TEXT_PREVIEW_BYTES = 512 * 1024; // 512KB

/**
 * Preview kinds whose blob object URL may be opened in a top-level browsing
 * context (new tab). A blob URL is still rendered by the browser there, so only
 * inert raster images and PDFs are allowed; markup/active content is never
 * eligible.
 */
export const SUPPORT_TOP_LEVEL_PREVIEW_KINDS: ReadonlySet<SupportAttachmentPreviewKind> = new Set([
  'image',
  'pdf'
]);

/**
 * Normalize a Content-Type header/blob type to its bare MIME (strip charset etc.).
 */
export function normalizeContentType(contentType?: string | null): string {
  return (contentType || '').split(';')[0].trim().toLowerCase();
}

/**
 * Cross-check a blob's declared content-type against the allow-list for a kind.
 *
 * Text is intentionally permissive on empty/octet-stream because many servers
 * return generic types for text files; it is still rendered as INERT text via
 * text interpolation (never parsed as markup). However, if the server actively
 * declares the blob as markup/active content (text/html, image/svg+xml,
 * JS/PHP/WASM, …) we refuse the preview outright rather than risk any renderer
 * ever interpreting it — a defense-in-depth layer on top of the inert-text sink.
 */
export function isSupportAttachmentContentTypeAllowed(
  kind: SupportAttachmentPreviewKind,
  contentType?: string | null
): boolean {
  const mime = normalizeContentType(contentType);
  if (!mime) return kind === 'text';
  if (SUPPORT_ATTACHMENT_ACTIVE_MIME_TYPES.has(mime)) return false;
  if (kind === 'text') {
    return mime.startsWith('text/') || SUPPORT_ATTACHMENT_PREVIEW_MIME.text.includes(mime);
  }
  return SUPPORT_ATTACHMENT_PREVIEW_MIME[kind].includes(mime);
}

/**
 * Strip control characters that could corrupt rendering or smuggle
 * terminal/ANSI escape sequences and invisible payloads into the preview
 * surface. Tab, line feed, and carriage return are preserved so formatting is
 * kept; every other C0/C1 control character (including NUL and the ESC that
 * starts ANSI sequences) is removed.
 *
 * Invisible/format characters are also stripped:
 * - `\u200B-\u200F` zero-width space/non-joiner/joiner and LRM/RLM marks
 * - `\u202A-\u202E` bidi embedding/override controls (LRE/RLE/PDF/LRO/RLO)
 * - `\u2066-\u2069` bidi isolate controls (LRI/RLI/FSI/PDI)
 * - `\u2028\u2029` line/paragraph separators
 * - `\uFEFF` zero-width no-break space / BOM
 *
 * The bidi controls are the "Trojan Source" spoofing vector: they can visually
 * reorder text so previewed file content reads differently than its bytes, so
 * they must never survive into the rendered preview.
 */
export const sanitizeSupportPreviewText = (value: string): string =>
  value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
    .replace(/[\u200B-\u200F\u202A-\u202E\u2028\u2029\u2066-\u2069\uFEFF]/g, '');

/**
 * Only same-origin `blob:` object URLs (the ones this app itself minted via
 * URL.createObjectURL) may be handed to window.open. Any other scheme
 * (javascript:, data:, http(s):, vbscript:, …) is rejected outright so a
 * corrupted or attacker-influenced URL can never reach a top-level navigation.
 */
export const isSafeSupportPreviewObjectUrl = (url: string | null | undefined): boolean =>
  typeof url === 'string' && url.startsWith('blob:');

/**
 * Whether a resolved preview may be opened in a new tab. Requires an inert
 * top-level kind (image/pdf), a ready status, and a concrete same-origin blob
 * object URL. Text previews are never eligible (they carry no object URL), which
 * prevents a regression from ever surfacing a mislabeled text/HTML/SVG blob into
 * a new tab.
 */
export const canOpenSupportPreviewInNewTab = (
  kind: SupportAttachmentPreviewKind | null | undefined,
  objectUrl: string | null | undefined,
  status: 'loading' | 'ready' | 'error'
): boolean =>
  status === 'ready' &&
  !!kind &&
  SUPPORT_TOP_LEVEL_PREVIEW_KINDS.has(kind) &&
  isSafeSupportPreviewObjectUrl(objectUrl);
